import { clients } from "../lib/https";
import { ConfigurationSettings } from "../models/configuration-settings";
import { FileMetadata } from "/features/chat/types/chat";

export interface TitleResponse {
  conversationTitle: string;
  conversation_id: string;
}

// Message Update Types
export interface MessageComment {
  id: string;
  content: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
}

export interface MessageAttachment {
  fileId: string;
  fileName: string;
  extension: string;
  createdAt?: string;
  updatedAt?: string;
  size?: number;
}

export interface MessageReaction {
  emoji: string;
  userIds: string[];
}

export interface MessageQueryMetadata {
  searchOnline?: boolean;
  isRegenerate?: boolean;
  imageGeneration?: boolean;
  integrationSources?: string[];
}

export interface UpdateMessagePayload {
  conversationId: string;
  messageId: string;
  content: string;
  comments?: MessageComment[];
  attachments?: MessageAttachment[];
  parentIds?: string[];
  mentions?: string[];
  reactions?: Record<string, MessageReaction>;
  queryMetadata?: MessageQueryMetadata;
}

export interface UpdateMessageResponse {
  success: boolean;
  message?: string;
  id?: string;
  content?: string;
  type?: string;
  senderId?: string;
  comments?: any;
  attachments?: any;
  parentIds?: any;
  editHistory?: any;
  mentions?: any;
  reactions?: any;
  createdAt?: string;
  updatedAt?: string;
  metadata?: any;
  status?: any;
  originalMessageId?: string;
}


/**
 * Updates a collaboration message with new content, attachments, mentions, etc.
 *
 * @param {UpdateMessagePayload} payload - The update payload containing message details.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<UpdateMessageResponse>} The response from the collaboration API.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await updateMessage({
 *   conversationId: 'conv123',
 *   messageId: 'msg456',
 *   content: 'Updated message content',
 *   parentIds: ['parentMsgId'],
 * }, config);
 */
export const updateMessage = async (
  payload: UpdateMessagePayload,
  config: ConfigurationSettings
): Promise<UpdateMessageResponse> => {
  const baseUrl = `https://${config.paths.taskforceBaseUrl}`;
  // Use collaboration path + /messages for the update endpoint
  const url = `${baseUrl}${config.paths.taskforce.collaboration}/messages`;

  return clients.put<UpdateMessageResponse>(url, config.blocksKey, JSON.stringify(payload));
};

export const getSSEStream = async (messageBody: string, config: ConfigurationSettings) => {
  const baseUrl = `https://${config.paths.taskforceBaseUrl}`;
  const url = `${baseUrl}${config.paths.taskforce.chatStream}`;
  return clients.post(url, config.blocksKey, messageBody) as Promise<Response>;
}

export const getTitle2 = async (conversationId: string, config: ConfigurationSettings) => {
  const baseUrl = `https://${config.paths.taskforceBaseUrl}`;
  const url = `${baseUrl}${config.paths.taskforce.collaborationConversation}/${conversationId}/title/generate`;
  return clients.get(url, config.blocksKey) as Promise<TitleResponse>;
}

export const getSearchRelatedQuestionList = async (
  data: {
    conversationId: string;
    messageId: string;
    type: string;
    modelName: string;
  },
  config: ConfigurationSettings
) => {
  const payload = {
    ...data
  };

  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.taskforce.suggestions}`;
  return clients.post(url, config.blocksKey, JSON.stringify(payload));
};

export const getGroupChatStream = async (
  messageBody: string,
  config: ConfigurationSettings,
  conversationId: string,
  attachmentsMetadata?: FileMetadata[],
  parentIds?: string[],
  mentions?: string[],
  isRegenerate?: boolean,
  imageGeneration?: boolean,
  integrationSources?: string[],
  searchOnline?: boolean,
) => {
  const payload = {
    conversationId,
    message: {
      content: messageBody,
      attachments: attachmentsMetadata || [],
      ...(parentIds && { parentIds }),
      ...(mentions && { mentions }),
      queryMetadata: {
        searchOnline: searchOnline ?? false,
        isRegenerate: isRegenerate ?? false,
        imageGeneration: imageGeneration ?? false,
        integrationSources: integrationSources ?? []
    }
    },
   
  };

  // console.log('attachments in getGroupChatStream', attachments,"payload:", payload)

  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.taskforce.collaboration}`;
  // const url = "http://localhost:8000/api/collaboration"
  return clients.post(url, config.blocksKey, JSON.stringify(payload)) as Promise<Response>;
}

export const stopGeneration = async (conversationId: string, assistantMessageId: string, config: ConfigurationSettings) => {
  const payload = {
    conversationId,
    assistantMessageId
  }
  
  const baseUrl = `https://${config.paths.taskforceBaseUrl}`;
  const url = `${baseUrl}${config.paths.taskforce.collaboration}/cancel`;
  return clients.post(url, config.blocksKey, JSON.stringify(payload)) as Promise<Response>;
}

